import {Loader2} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {Badge} from '@common-web/components/ui/badge';
import {Sheet, SheetContent, SheetHeader, SheetTitle} from '@common-web/components/ui/sheet';
import {STAGE_META, type StageKey} from '@internal/pages/claims/stageMeta';

function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function fmtDateRange(from: Date | null | undefined, thru: Date | null | undefined): string {
  const a = fmtDate(from);
  const b = fmtDate(thru);
  if (a === '—' && b === '—') return '—';
  return a === b ? a : `${a} – ${b}`;
}

export function ClaimDetailSheet({
  batchNumber,
  batchClaim,
  open,
  onOpenChange,
}: {
  batchNumber: number | null;
  batchClaim: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {data, isLoading, isError} = trpc.internal.claimsTracking.consolidated.getClaimDetail.useQuery(
    {batchNumber: batchNumber ?? 0, batchClaim: batchClaim ?? 0},
    {enabled: open && batchNumber != null && batchClaim != null},
  );

  const headerEx = data?.exCodes.filter((x) => x.level === 'HEADER') ?? [];
  const lineEx = data?.exCodes.filter((x) => x.level === 'LINE') ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full sm:max-w-xl overflow-y-auto'>
        <SheetHeader>
          <SheetTitle className='font-mono'>
            {batchNumber != null ? `Batch ${batchNumber} / ${batchClaim}` : 'Claim'}
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className='flex justify-center py-12'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        )}
        {isError && <p className='text-sm text-destructive py-6'>Could not load claim from VBA.</p>}

        {data && (
          <div className='space-y-6 mt-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <Badge variant='outline' className='font-medium'>
                {STAGE_META[data.stage as StageKey]?.label ?? data.stage}
              </Badge>
              <Badge variant='outline' className='font-mono'>
                {data.origin === 'claim' ? 'Claim' : 'ClaimBatch'}
              </Badge>
              <Badge variant='outline' className='font-mono'>
                {data.status ?? '—'}
                {data.statusDescription ? ` · ${data.statusDescription}` : ''}
              </Badge>
            </div>

            <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
              <dt className='text-muted-foreground'>Claim ID</dt>
              <dd className='font-mono'>{data.claimId ?? '—'}</dd>
              <dt className='text-muted-foreground'>Claim #</dt>
              <dd className='font-mono'>{data.claimNumber ?? '—'}</dd>
              <dt className='text-muted-foreground'>Group</dt>
              <dd>{data.groupName ?? data.groupId ?? '(No Group ID)'}</dd>
              <dt className='text-muted-foreground'>Plan</dt>
              <dd>{data.planName ?? data.planId ?? '—'}</dd>
              <dt className='text-muted-foreground'>Plan year</dt>
              <dd className='font-mono'>{data.planYear?.trim() || '—'}</dd>
              <dt className='text-muted-foreground'>Patient</dt>
              <dd>{data.patientName ?? '—'}</dd>
              <dt className='text-muted-foreground'>Insured Policy #</dt>
              <dd className='font-mono'>
                {data.insuredPolicyNumber?.trim() || (data.origin === 'claim' ? '(adjudicated — not stored on Claim)' : '—')}
              </dd>
              <dt className='text-muted-foreground'>Subscriber</dt>
              <dd className='font-mono'>{data.subscriberId ?? '—'}</dd>
              <dt className='text-muted-foreground'>Payor</dt>
              <dd>{data.payorId ?? '—'}</dd>
              <dt className='text-muted-foreground'>Received</dt>
              <dd>{fmtDate(data.receivedDate)}</dd>
              <dt className='text-muted-foreground'>Service</dt>
              <dd>{fmtDate(data.serviceDate)}</dd>
              <dt className='text-muted-foreground'>Provider ID</dt>
              <dd className='font-mono'>{data.providerId ?? '—'}</dd>
              <dt className='text-muted-foreground'>Provider taxonomy</dt>
              <dd className='font-mono'>
                {data.providerTaxonomy?.trim() || (data.origin === 'claim' ? '(not stored on Claim)' : '—')}
              </dd>
              <dt className='text-muted-foreground'>Funded status</dt>
              <dd>{data.claimFundedStatus ?? '—'}</dd>
              <dt className='text-muted-foreground'>Check batch</dt>
              <dd className='font-mono'>{data.checkBatch ?? '—'}</dd>
            </dl>

            {data.diagnosisCodes.length > 0 && (
              <DetailSection
                title={`Diagnosis codes${data.diagnosticCodeType && data.diagnosticCodeType !== '0' ? ` (${data.diagnosticCodeType})` : ''}`}
              >
                <div className='flex flex-wrap gap-1.5'>
                  {data.diagnosisCodes.map((code) => (
                    <Badge key={code} variant='outline' className='font-mono'>
                      {code}
                    </Badge>
                  ))}
                </div>
              </DetailSection>
            )}

            {data.lineItems.length > 0 && (
              <DetailSection title='Service lines'>
                <div className='space-y-2'>
                  {data.lineItems.map((line, i) => (
                    <div key={`${line.claimSeq}-${i}`} className='rounded-md border px-3 py-2 text-sm'>
                      <div className='flex items-center gap-2 flex-wrap mb-1'>
                        <span className='font-mono text-xs text-muted-foreground'>Line {line.claimSeq}</span>
                        {line.placeOfService && (
                          <Badge variant='outline' className='text-xs'>
                            POS: {line.placeOfService}
                          </Badge>
                        )}
                        {line.diagnosisCode && (
                          <Badge variant='outline' className='font-mono text-xs'>
                            Dx: {line.diagnosisCode}
                          </Badge>
                        )}
                      </div>
                      <dl className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs'>
                        <dt className='text-muted-foreground'>Procedure code</dt>
                        <dd className='font-mono'>
                          {[line.procedureCode, line.procedureCode2].filter(Boolean).join(' / ') || '—'}
                        </dd>
                        <dt className='text-muted-foreground'>Service dates</dt>
                        <dd>{fmtDateRange(line.serviceDate, line.serviceThru)}</dd>
                      </dl>
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}

            {data.errFlags.length > 0 && (
              <DetailSection title='ERR validation flags'>
                <ul className='space-y-2'>
                  {data.errFlags.map((f) => (
                    <li key={`${f.errorType}-${f.errorCode}`} className='rounded-md border px-3 py-2 text-sm'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <Badge variant='outline' className='font-mono bg-red-50 text-red-700 border-red-200'>
                          {f.errorCode}
                        </Badge>
                        <span className='font-mono text-xs text-muted-foreground'>{f.errorType}</span>
                      </div>
                      <p className='mt-1 text-muted-foreground'>{f.description ?? 'No description'}</p>
                    </li>
                  ))}
                </ul>
              </DetailSection>
            )}

            <ExCodeSection title='Header ex codes' rows={headerEx} />
            <ExCodeSection title='Line ex codes' rows={lineEx} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailSection({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <div>
      <h3 className='text-sm font-semibold text-brand-navy mb-2'>{title}</h3>
      {children}
    </div>
  );
}

type ExCode = {
  level: string;
  claimSeq: string | null;
  exCode: string;
  description: string | null;
  cleared: boolean;
  source: string | null;
  comment: string | null;
};

function ExCodeSection({title, rows}: {title: string; rows: ExCode[]}) {
  return (
    <DetailSection title={title}>
      {rows.length === 0 ? (
        <p className='text-sm text-muted-foreground'>None.</p>
      ) : (
        <ul className='space-y-2'>
          {rows.map((x, i) => (
            <li
              key={`${x.exCode}-${x.claimSeq ?? ''}-${i}`}
              className={cn('rounded-md border px-3 py-2 text-sm', x.cleared && 'opacity-60')}
            >
              <div className='flex items-center gap-2 flex-wrap'>
                <Badge variant='outline' className='font-mono'>
                  {x.exCode}
                </Badge>
                {x.claimSeq ? (
                  <span className='text-xs text-muted-foreground font-mono'>Line {x.claimSeq}</span>
                ) : null}
                <Badge variant={x.cleared ? 'secondary' : 'outline'} className='text-xs'>
                  {x.cleared ? 'Cleared' : 'Open'}
                </Badge>
                {x.source ? <span className='text-xs text-muted-foreground'>{x.source}</span> : null}
              </div>
              <p className='mt-1 text-muted-foreground'>{x.description ?? 'No description'}</p>
              {x.comment ? <p className='mt-1 text-xs text-muted-foreground italic'>{x.comment}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}
